import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { Button } from '@arcanecircle/ui/grimoire/button'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Rendered in place of the subtree when a render throws. When omitted a
   *  compact default card is shown. Receives the error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => ReactNode
  /** Optional label used in the console log to identify which boundary tripped. */
  label?: string
}

interface ErrorBoundaryState {
  error: Error | null
}

/**
 * Catches render-time exceptions in its subtree so a single malformed piece
 * of data (e.g. a character sheet whose stats arrive in an unexpected shape)
 * degrades to an inline message instead of unmounting the whole React tree
 * and leaving the user staring at a blank page.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[ErrorBoundary${this.props.label ? `:${this.props.label}` : ''}]`, error, info)
  }

  reset = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (error) {
      if (this.props.fallback) return this.props.fallback(error, this.reset)
      return (
        <div
          role="alert"
          style={{
            padding: '0.75rem 1rem',
            margin: '0.5rem',
            borderRadius: 8,
            border: '1px solid hsl(var(--ac-danger, 0 70% 50%))',
            background: 'hsl(var(--ac-surface, 0 0% 10%))',
            color: 'hsl(var(--ac-text-light, 0 0% 90%))',
            fontSize: '0.8125rem',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Something went wrong here.</div>
          <div style={{ opacity: 0.7, marginBottom: 8 }}>{error.message}</div>
          <Button size="sm" variant="outline" onClick={this.reset}>
            Try again
          </Button>
        </div>
      )
    }
    return this.props.children
  }
}
